import { RowData } from '../components/TwAdvancedRates/RateRow';
import { FactoringStatus } from '../providers/Invoices/Invoices.model';
import { InvoiceSubjectLine } from '../providers/User/User.model';
import {
  DefaultPaymentTerms,
  InvoiceService,
  InvoiceServiceUnit
} from '../redux/api/invoiceService/invoiceService.model';
import DateUtils from './dateUtils';
import NumberUtils from './numberUtils';

class InvoiceUtils {
  static getInvoiceSubjectLineLabel(subjectLine: InvoiceSubjectLine): string {
    switch (subjectLine) {
      case InvoiceSubjectLine.INVOICE_NUMBER:
        return 'Invoice number';
      case InvoiceSubjectLine.CUSTOMER_LOAD_NUMBER:
        return 'Customer load number';
      case InvoiceSubjectLine.INVOICE_NUMBER_AND_CUSTOMER_LOAD:
        return 'Invoice number & Customer load number';
      default:
        return 'Invoice number';
    }
  }

  static getDefaultInvoiceDateLabel(invoiceDateOption: string): string {
    switch (invoiceDateOption) {
      case 'INVOICE_CREATION_DATE':
        return 'Invoice creation date';
      case 'LOAD_PICKUP_DATE':
        return 'Load pickup date';
      case 'LOAD_DELIVERY_DATE':
        return 'Load delivery date';
      default:
        return 'Invoice creation date';
    }
  }

  static getDaysFromPaymentTerms = (paymentTerms: DefaultPaymentTerms): number => {
    switch (paymentTerms) {
      case DefaultPaymentTerms.WITHIN_15_DAYS:
        return 15;

      case DefaultPaymentTerms.WITHIN_30_DAYS:
        return 30;

      case DefaultPaymentTerms.WITHIN_45_DAYS:
        return 45;

      case DefaultPaymentTerms.WITHIN_60_DAYS:
        return 60;

      case DefaultPaymentTerms.UPON_RECEIPT:
        return 0;

      default:
        return 30;
    }
  };

  static getQuantityBasedOnUnitAndInvoiceSource({
    service,
    totalLoadedMiles,
    amountOfDefaultService,
    isQboEnabled,
    unit,
    overwrittenMileage
  }: {
    service?: InvoiceService;
    totalLoadedMiles: number;
    amountOfDefaultService: number;
    isQboEnabled: boolean;
    unit?: InvoiceServiceUnit;
    overwrittenMileage?: number;
  }): number | undefined {
    const unitToUse = unit || service?.unit;
    if ((isQboEnabled && service?.isDefault) || unitToUse === InvoiceServiceUnit.FLAT) {
      return 1;
    }

    if (unitToUse === InvoiceServiceUnit.PER_MILE) {
      return overwrittenMileage || totalLoadedMiles || 0;
    }

    if (unitToUse === InvoiceServiceUnit.PERCENTAGE) {
      return amountOfDefaultService || 0;
    }

    return undefined;
  }

  static calculateDefaultServiceAmount({
    rows,
    defaultServiceId
  }: {
    rows: RowData[];
    defaultServiceId: string;
  }): number {
    let amount = 0;
    for (const service of rows) {
      const isQboService = service.qbo_service_id && service.qbo_service_id !== '';
      const isInvoiceService = service.invoice_service_id && service.invoice_service_id !== '';

      if (!isQboService && isInvoiceService && service.invoice_service_id === defaultServiceId) {
        amount += service.rate || 0;
      }

      if (isQboService && service.qbo_service_id === defaultServiceId) {
        amount += service.rate || 0;
      }
    }

    return amount;
  }

  /**
   * This function calculates the amount of a row
   * if unit is flat, return rate since it's a fixed amount
   * if unit is percentage and service is not QBO divide rate by 100 and multiply by linehaul amount of default service
   * if service is QBO or unit is different than flat multiply rate by quantity
   *
   * @param params - rate, quantity, unit, isQboService, linehaulAmountOfDefaultService
   * @returns number - amount of the row
   */
  static calculateRowAmount({
    rate,
    quantity,
    unit,
    isQboService
  }: {
    rate: number;
    quantity: number;
    unit: InvoiceServiceUnit;
    isQboService?: boolean;
  }): number {
    // If unit is flat, return rate since it's a fixed amount
    if (unit === InvoiceServiceUnit.FLAT) {
      return rate || 0;
    }
    // If unit is percentage and service is not QBO
    // divide rate by 100 and multiply by linehaul amount of default service
    else if (unit === InvoiceServiceUnit.PERCENTAGE && !isQboService) {
      // calculate the amount with precision to see to 4 decimal points on the row level
      return NumberUtils.calculateCurrencyAmountWithPrecision(rate / 100, quantity);
    }
    // if service is QBO
    // or unit is different than flat
    // multiply rate by quantity
    // QBO services are always quantity based
    else {
      // calculate the amount with precision to see to 4 decimal points on the row level
      return NumberUtils.calculateCurrencyAmountWithPrecision(rate, quantity);
    }
  }

  static getFactoredTooltipMsg(factoringStatus: FactoringStatus): string {
    return `Sent to ${factoringStatus.factoring_company} - ${DateUtils.getMediumDate(
      new Date(factoringStatus.status_updated_at)
    )}`;
  }

  static getInvoiceBlobTitle(invoiceId: string): string {
    return `Invoice #${invoiceId}`;
  }

  static getInvoiceDownloadName(invoiceId: string): string {
    return `#${invoiceId}-invoice.pdf`;
  }
}

export default InvoiceUtils;
